import { useInView } from '../hooks/useInView';
import { contactInfo } from '../data/mock';
import { MapPin, Phone, Mail, Clock, MessageCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

export const LocationSection = ({ onBookDemo }) => {
  const [ref, isInView] = useInView();

  return (
    <section id="contact" className="relative py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <div
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-[#D4A853] font-semibold text-sm uppercase tracking-[0.2em]">Visit Us</span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#0A1628] mt-3 mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Let's Meet & Plan Your Future
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Visit our office in DDU Nagar, Amanaka, Raipur for a free counseling session.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Map */}
          <div
            className={`rounded-2xl overflow-hidden shadow-lg transition-[opacity,transform] duration-700 ${
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <iframe
              src={contactInfo.mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '480px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Chris IELTS Consulting Location"
            />
          </div>

          {/* Contact Panel */}
          <div
            className={`transition-[opacity,transform] duration-700 delay-200 ${
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="bg-[#0A1628] rounded-2xl p-8 lg:p-10 h-full flex flex-col">
              <h3 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>Get in Touch</h3>

              <div className="space-y-6 flex-1">
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853]/20 transition-colors duration-300">
                    <MapPin className="w-5 h-5 text-[#E8C87A]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Office Address</p>
                    <a
                      href={contactInfo.googleMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/50 text-sm leading-relaxed hover:text-[#E8C87A] transition-colors duration-300 flex items-center gap-1"
                    >
                      {contactInfo.address}
                      <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853]/20 transition-colors duration-300">
                    <Phone className="w-5 h-5 text-[#E8C87A]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Phone</p>
                    {contactInfo.phones.map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone.replace(/\s/g, '')}`}
                        className="text-white/50 text-sm hover:text-[#E8C87A] transition-colors duration-300 block"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853]/20 transition-colors duration-300">
                    <Mail className="w-5 h-5 text-[#E8C87A]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Email</p>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-white/50 text-sm hover:text-[#E8C87A] transition-colors duration-300"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853]/20 transition-colors duration-300">
                    <Clock className="w-5 h-5 text-[#E8C87A]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Working Hours</p>
                    <p className="text-white/50 text-sm">{contactInfo.hours}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={onBookDemo}
                  className="bg-[#D4A853] hover:bg-[#C9A84C] text-[#0A1628] font-semibold flex-1 rounded-full shadow-lg shadow-[#D4A853]/20 transition-[background-color,box-shadow] duration-300"
                >
                  Book Free Counseling
                </Button>
                <a
                  href={`https://wa.me/${contactInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 w-full rounded-full transition-[background-color,border-color] duration-300"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
